import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CheckCircle } from '@mui/icons-material'
import { Alert, Box, Button, ButtonBase, Chip, Dialog, DialogActions, DialogContent, Skeleton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { FtueVideoThumbnail } from '../../../api/core'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { borderRadiusDefault } from '../../../themes/theme'
import { useGetVimeoPicturesQuery } from '../../api/vimeo'
import { isSamePicture, pickThumbnailForWidth, VimeoPicture } from '../../helpers/vimeo'

type VimeoPicturePickerDialogProps = {
  open: boolean
  vimeoId: string
  // The video's thumbnails now, so the dialog can mark and preselect that picture.
  currentThumbnails?: FtueVideoThumbnail[]
  onClose: () => void
  onSelect: (thumbnails: FtueVideoThumbnail[]) => void
}

const PREVIEW_MIN_WIDTH = 295
const gridColumns = 'repeat(auto-fill, minmax(220px, 1fr))'

const errorStatus = (error: unknown) => (typeof error === 'object' && error !== null && 'status' in error ? (error as { status: unknown }).status : undefined)

const initialPictureId = (pictures: VimeoPicture[], current?: FtueVideoThumbnail[]) =>
  (pictures.find((picture) => isSamePicture(picture.thumbnails, current)) ?? pictures.find((picture) => picture.active) ?? pictures[0])?.pictureId

export const VimeoPicturePickerDialog: FC<VimeoPicturePickerDialogProps> = ({ open, vimeoId, currentThumbnails, onClose, onSelect }) => {
  const { t } = useTranslation()
  // Refetched on every opening: the pictures can change in Vimeo while the page is open.
  const picturesQuery = useGetVimeoPicturesQuery(vimeoId, { skip: !open || !vimeoId, refetchOnMountOrArgChange: true })
  const pictures = useMemo(() => picturesQuery.data ?? [], [picturesQuery.data])
  const [selectedId, setSelectedId] = useState<string>()

  // Only when opening or when the pictures arrive, so a parent passing a new array doesn't undo a pick.
  const initialThumbnails = useRef(currentThumbnails)
  initialThumbnails.current = currentThumbnails
  useEffect(() => {
    if (open) setSelectedId(initialPictureId(pictures, initialThumbnails.current))
  }, [open, pictures])

  const selected = pictures.find((picture) => picture.pictureId === selectedId)
  const isCurrent = (picture: VimeoPicture) => isSamePicture(picture.thumbnails, currentThumbnails)

  const handleUse = () => {
    if (selected) onSelect(selected.thumbnails)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <GRDialogTitle onClose={onClose}>{t('internal-common:vimeo_picker_title')}</GRDialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('internal-common:vimeo_picker_intro')}
          </Typography>
          {picturesQuery.isFetching ? (
            <Box display="grid" gridTemplateColumns={gridColumns} gap={2}>
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} variant="rectangular" sx={{ aspectRatio: '16 / 9', height: 'auto', borderRadius: borderRadiusDefault }} />
              ))}
            </Box>
          ) : picturesQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => picturesQuery.refetch()}>
                  {t('internal-common:vimeo_picker_retry')}
                </Button>
              }
            >
              {errorStatus(picturesQuery.error) === 404
                ? t('internal-common:vimeo_picker_not_found', { id: vimeoId })
                : t('internal-common:vimeo_picker_failed')}
            </Alert>
          ) : !pictures.length ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {t('internal-common:vimeo_picker_none')}
            </Typography>
          ) : (
            <Box display="grid" gridTemplateColumns={gridColumns} gap={2} role="listbox" aria-label={t('internal-common:vimeo_picker_title')}>
              {pictures.map((picture, index) => {
                const isSelected = picture.pictureId === selectedId
                const preview = pickThumbnailForWidth(picture.thumbnails, PREVIEW_MIN_WIDTH)
                const label = t('internal-common:vimeo_picker_picture', { index: index + 1 })
                return (
                  <ButtonBase
                    key={picture.pictureId}
                    role="option"
                    aria-selected={isSelected}
                    aria-label={label}
                    onClick={() => setSelectedId(picture.pictureId)}
                    sx={{
                      position: 'relative',
                      display: 'block',
                      textAlign: 'left',
                      borderRadius: borderRadiusDefault,
                      overflow: 'hidden',
                      border: (theme) => `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                      boxShadow: (theme) => (isSelected ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'),
                    }}
                  >
                    {preview && (
                      <Box component="img" src={preview.link} alt={label} sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }} />
                    )}
                    {isSelected && (
                      <CheckCircle color="primary" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', borderRadius: '50%' }} />
                    )}
                    <Stack direction="row" spacing={1} p={1} minHeight={40} alignItems="center" flexWrap="wrap" useFlexGap>
                      {isCurrent(picture) && <Chip size="small" color="primary" label={t('internal-common:vimeo_picker_current')} />}
                      {picture.active && <Chip size="small" variant="outlined" label={t('internal-common:vimeo_picker_active')} />}
                    </Stack>
                  </ButtonBase>
                )
              })}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('internal-common:cancel')}</Button>
        <Button variant="contained" onClick={handleUse} disabled={!selected || picturesQuery.isFetching || isCurrent(selected)}>
          {t('internal-common:vimeo_picker_use')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
